import { useMemo, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Panel,
  PanelBody,
  PanelHeader,
  SegmentedControl,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
  type MultiComboboxOption,
} from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import {
  achievedTone,
  compareDecimal,
  formatCount,
  formatPct,
  parseLineFieldInput,
} from '@/lib/sales-targets';
import type {
  SalesTargetPool,
  SalesTargetPoolKind,
  SalesTargetPoolUpdateInput,
  SalesTargetsView,
} from '@/lib/types';

/**
 * Sales targets → Pools (Masterclasses, DAC …). A pool carries the plan and
 * target for a set of member projects; the members keep their own achieved,
 * and their own plan/target leave the totals (the server does that maths).
 *
 * Writes: create / PATCH (changed keys only) / delete, and the member list is
 * a WHOLE-SET replace (`PUT …/projects`). Each step is its own request with no
 * transaction, so a create that lands while its member write fails is
 * reported as exactly that. After any write the parent re-reads the view.
 *
 * Money and counts are decimal strings end to end: inputs are validated as
 * text and sent as text, never through `Number()`. `null` renders `—`.
 */

interface Props {
  view: SalesTargetsView;
  canEdit: boolean;
  onChanged: () => void;
}

const KIND_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'revenue', label: 'Revenue' },
] as const satisfies readonly { value: SalesTargetPoolKind; label: string }[];

const KIND_LABEL: Record<SalesTargetPoolKind, string> = { count: 'Count', revenue: 'Revenue' };

/** A stored figure as the input's starting text ("80.00" → "80"); `null` → ''. */
function inputText(value: string | null | undefined): string {
  return value === null || value === undefined ? '' : formatCount(value);
}

function sameDecimal(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return compareDecimal(a, b) === 0;
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.formMessage ?? error.message;
  return error instanceof Error ? error.message : 'Something went wrong.';
}

function PercentBadge({ pool }: { pool: SalesTargetPool }) {
  if (pool.achievedPct === null) return <span className="text-fg-muted">—</span>;
  return (
    <Badge tone={achievedTone(pool)} className="tabular-nums">
      {formatPct(pool.achievedPct)}
    </Badge>
  );
}

function PlanFigure({ pool, which }: { pool: SalesTargetPool; which: 'planned' | 'target' }) {
  if (pool.kind === 'count') {
    const count = which === 'planned' ? pool.plannedCount : pool.targetCount;
    return <span className="tabular-nums">{formatCount(count)}</span>;
  }
  const amount = which === 'planned' ? pool.plannedRevenueUsd : pool.targetRevenueUsd;
  return <Money amount={amount} currency="USD" />;
}

export function SalesTargetPoolsCard({ view, canEdit, onChanged }: Props) {
  const [editing, setEditing] = useState<SalesTargetPool | 'new' | null>(null);
  const [deleting, setDeleting] = useState<SalesTargetPool | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const line of view.lines) map.set(line.projectId, line.projectName);
    return map;
  }, [view.lines]);

  const memberPills = (pool: SalesTargetPool) =>
    pool.projectIds.map((id) => ({ key: id, label: projectNames.get(id) ?? `Project ${id}` }));

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteSalesTargetPool(deleting.id);
      setDeleting(null);
      onChanged();
    } catch (error) {
      setDeleteError(errorText(error));
    } finally {
      setDeleteBusy(false);
    }
  }

  const actions = (pool: SalesTargetPool) =>
    canEdit ? (
      <div className="flex items-center justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={() => setEditing(pool)}>
          Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDeleteError(null);
            setDeleting(pool);
          }}
        >
          Delete
        </Button>
      </div>
    ) : null;

  return (
    <Panel aria-labelledby="sales-target-pools-title">
      <PanelHeader
        id="sales-target-pools-title"
        title="Pools"
        actions={
          canEdit ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
              Add pool
            </Button>
          ) : undefined
        }
      />
      {view.pools.length === 0 ? (
        <PanelBody>
          <EmptyState
            title="No pools for this cycle"
            description={canEdit ? 'Add a pool to group projects under one plan and target.' : undefined}
          />
        </PanelBody>
      ) : (
        <>
          <div className="hidden md:block">
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>Pool</TH>
                    <TH>Kind</TH>
                    <TH>Projects</TH>
                    <TH numeric>Planned</TH>
                    <TH numeric>Target</TH>
                    <TH numeric>Unit price</TH>
                    <TH numeric>Target revenue</TH>
                    <TH numeric>Achieved</TH>
                    <TH numeric>Behind</TH>
                    <TH numeric>%</TH>
                    {canEdit && (
                      <TH>
                        <span className="sr-only">Actions</span>
                      </TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {view.pools.map((pool) => (
                    <TR key={pool.id}>
                      <TD className="font-medium text-fg">{pool.name}</TD>
                      <TD>{KIND_LABEL[pool.kind]}</TD>
                      <TD className="min-w-48">
                        <ValuePills items={memberPills(pool)} />
                      </TD>
                      <TD numeric>
                        <PlanFigure pool={pool} which="planned" />
                      </TD>
                      <TD numeric>
                        <PlanFigure pool={pool} which="target" />
                      </TD>
                      <TD numeric>
                        <Money amount={pool.kind === 'count' ? pool.unitPriceUsd : null} currency="USD" />
                      </TD>
                      <TD numeric>
                        <Money amount={pool.targetRevenueUsd} currency="USD" />
                      </TD>
                      <TD numeric>
                        <Money amount={pool.achievedUsd} currency="USD" />
                      </TD>
                      <TD numeric>
                        <Money amount={pool.gapUsd} currency="USD" />
                      </TD>
                      <TD numeric>
                        <PercentBadge pool={pool} />
                      </TD>
                      {canEdit && <TD>{actions(pool)}</TD>}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          <ul className="divide-y divide-border md:hidden">
            {view.pools.map((pool) => (
              <li key={pool.id} className="space-y-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium break-words text-fg">{pool.name}</p>
                    <p className="text-xs text-fg-muted">{KIND_LABEL[pool.kind]}</p>
                  </div>
                  <PercentBadge pool={pool} />
                </div>
                <ValuePills items={memberPills(pool)} />
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <dt className="text-fg-muted">Planned</dt>
                  <dd className="text-right">
                    <PlanFigure pool={pool} which="planned" />
                  </dd>
                  <dt className="text-fg-muted">Target</dt>
                  <dd className="text-right">
                    <PlanFigure pool={pool} which="target" />
                  </dd>
                  <dt className="text-fg-muted">Unit price</dt>
                  <dd className="text-right">
                    <Money amount={pool.kind === 'count' ? pool.unitPriceUsd : null} currency="USD" />
                  </dd>
                  <dt className="text-fg-muted">Target revenue</dt>
                  <dd className="text-right">
                    <Money amount={pool.targetRevenueUsd} currency="USD" />
                  </dd>
                  <dt className="text-fg-muted">Achieved</dt>
                  <dd className="text-right">
                    <Money amount={pool.achievedUsd} currency="USD" />
                  </dd>
                  <dt className="text-fg-muted">Behind</dt>
                  <dd className="text-right">
                    <Money amount={pool.gapUsd} currency="USD" />
                  </dd>
                </dl>
                {actions(pool)}
              </li>
            ))}
          </ul>
        </>
      )}

      {editing !== null && (
        <PoolFormModal
          key={editing === 'new' ? 'new' : editing.id}
          view={view}
          pool={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
          onPartial={onChanged}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        onConfirm={() => void confirmDelete()}
        title="Delete pool"
        description={
          deleting ? (
            <>
              Delete the pool <strong>{deleting.name}</strong> from {view.cycle.name}? Its plan and target
              are removed, and its {deleting.projectIds.length === 1 ? 'member project' : 'member projects'}{' '}
              count their own planned and target figures in the totals again.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        tone="danger"
        loading={deleteBusy}
        error={deleteError}
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------------- */

const FIELD_KEYS = [
  'name',
  'kind',
  'plannedCount',
  'targetCount',
  'unitPriceUsd',
  'plannedRevenueUsd',
  'targetRevenueUsd',
  'projectIds',
] as const;

type NumericKey = 'plannedCount' | 'targetCount' | 'unitPriceUsd' | 'plannedRevenueUsd' | 'targetRevenueUsd';

const COUNT_KEYS: readonly NumericKey[] = ['plannedCount', 'targetCount', 'unitPriceUsd'];
const REVENUE_KEYS: readonly NumericKey[] = ['plannedRevenueUsd', 'targetRevenueUsd'];

const NUMBER_LABEL: Record<NumericKey, string> = {
  plannedCount: 'Planned count',
  targetCount: 'Target count',
  unitPriceUsd: 'Unit price (USD)',
  plannedRevenueUsd: 'Planned revenue (USD)',
  targetRevenueUsd: 'Target revenue (USD)',
};

function PoolFormModal({
  view,
  pool,
  onClose,
  onSaved,
  onPartial,
}: {
  view: SalesTargetsView;
  pool: SalesTargetPool | null;
  onClose: () => void;
  onSaved: () => void;
  /** A step landed but a later one failed: the view must still re-read. */
  onPartial: () => void;
}) {
  const [name, setName] = useState(pool?.name ?? '');
  const [kind, setKind] = useState<SalesTargetPoolKind>(pool?.kind ?? 'count');
  const [numbers, setNumbers] = useState<Record<NumericKey, string>>({
    plannedCount: inputText(pool?.plannedCount),
    targetCount: inputText(pool?.targetCount),
    unitPriceUsd: inputText(pool?.unitPriceUsd),
    plannedRevenueUsd: inputText(pool?.plannedRevenueUsd),
    targetRevenueUsd: inputText(pool?.targetRevenueUsd),
  });
  const [projectIds, setProjectIds] = useState<string[]>(() => (pool?.projectIds ?? []).map(String));
  /** Set once a create has landed, so a retry only redoes the member write. */
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const options = useMemo<MultiComboboxOption[]>(() => {
    const otherPool = new Map<number, string>();
    for (const p of view.pools) {
      if (pool && p.id === pool.id) continue;
      for (const id of p.projectIds) otherPool.set(id, p.name);
    }
    return view.lines.map((line) => {
      const parts = [line.productName, line.intake, otherPool.get(line.projectId)].filter(
        (part): part is string => typeof part === 'string' && part !== '',
      );
      return {
        value: String(line.projectId),
        label: line.projectName,
        ...(parts.length > 0 ? { hint: parts.join(' · ') } : {}),
      };
    });
  }, [view.lines, view.pools, pool]);

  const activeKeys = kind === 'count' ? COUNT_KEYS : REVENUE_KEYS;

  function setNumber(key: NumericKey, value: string) {
    setNumbers((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const errors: Record<string, string> = {};
    const trimmedName = name.trim();
    if (trimmedName === '') errors.name = 'Enter a name.';

    /* Only the active kind's figures are read; the other kind's are cleared on a kind change. */
    const values: Partial<Record<NumericKey, string | null>> = {};
    for (const key of activeKeys) {
      const parsed = parseLineFieldInput(
        { kind: key === 'plannedCount' || key === 'targetCount' ? 'count' : 'usd', min: 0, max: null, decimals: 2 },
        numbers[key],
      );
      if (!parsed.ok) errors[key] = parsed.error;
      else values[key] = parsed.value;
    }

    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    const ids = projectIds.map(Number);
    setBusy(true);
    let landedId: number | null = pool?.id ?? createdId;
    let headerLanded = false;
    try {
      if (pool === null && createdId === null) {
        const created = await api.createSalesTargetPool({
          cycleId: view.cycle.id,
          name: trimmedName,
          kind,
          ...values,
        });
        landedId = created.id;
        headerLanded = true;
        setCreatedId(created.id);
      } else if (pool !== null) {
        const patch: SalesTargetPoolUpdateInput = {};
        if (trimmedName !== pool.name) patch.name = trimmedName;
        if (kind !== pool.kind) {
          patch.kind = kind;
          for (const key of kind === 'count' ? REVENUE_KEYS : COUNT_KEYS) patch[key] = null;
        }
        for (const key of activeKeys) {
          const next = values[key] ?? null;
          if (patch.kind !== undefined || !sameDecimal(next, pool[key])) patch[key] = next;
        }
        if (Object.keys(patch).length > 0) {
          await api.updateSalesTargetPool(pool.id, patch);
          headerLanded = true;
        }
      }

      const before = pool?.projectIds ?? [];
      const membersChanged = before.length !== ids.length || before.some((id) => !ids.includes(id));
      if (landedId !== null && membersChanged) {
        try {
          await api.setSalesTargetPoolProjects(landedId, ids);
        } catch (error) {
          const partial = headerLanded || createdId !== null;
          if (partial) onPartial();
          const routed = error instanceof ApiError ? error.routeFieldErrors(['projectIds']) : null;
          if (routed?.fields.projectIds) setFieldErrors({ projectIds: routed.fields.projectIds });
          const reason =
            routed && routed.unroutable.length > 0 ? routed.unroutable.join(' ') : errorText(error);
          setFormError(
            partial
              ? `${pool === null ? 'The pool was created' : 'The pool was saved'}, but its projects were not: ${reason}`
              : `The projects were not saved: ${reason}`,
          );
          return;
        }
      }
      onSaved();
    } catch (error) {
      if (error instanceof ApiError) {
        const routed = error.routeFieldErrors(FIELD_KEYS);
        setFieldErrors(routed.fields);
        const unroutable = routed.unroutable;
        setFormError(
          unroutable.length > 0
            ? unroutable.join(' ')
            : Object.keys(routed.fields).length > 0
              ? null
              : error.message,
        );
      } else {
        setFormError(errorText(error));
      }
    } finally {
      setBusy(false);
    }
  }

  const formId = 'sales-target-pool-form';

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={pool === null ? 'Add pool' : `Edit ${pool.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId} loading={busy}>
            {pool === null && createdId === null ? 'Create pool' : 'Save'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void submit(event)} className="grid gap-4" noValidate>
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}
        <Field label="Name" required error={fieldErrors.name}>
          {(props) => (
            <Input
              {...props}
              value={name}
              maxLength={120}
              invalid={Boolean(fieldErrors.name)}
              onChange={(event) => setName(event.target.value)}
              disabled={createdId !== null}
            />
          )}
        </Field>
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-fg-secondary">Kind</span>
          <SegmentedControl
            label="Kind"
            value={kind}
            options={KIND_OPTIONS}
            onChange={setKind}
            disabled={createdId !== null}
          />
          {fieldErrors.kind && <p className="text-xs text-danger">{fieldErrors.kind}</p>}
        </div>
        <div className={kind === 'count' ? 'grid gap-4 sm:grid-cols-3' : 'grid gap-4 sm:grid-cols-2'}>
          {activeKeys.map((key) => (
            <NumberField
              key={key}
              label={NUMBER_LABEL[key]}
              value={numbers[key]}
              error={fieldErrors[key]}
              disabled={createdId !== null}
              onChange={(value) => setNumber(key, value)}
            />
          ))}
        </div>
        <Field label="Projects" error={fieldErrors.projectIds}>
          {(props) => (
            <MultiCombobox
              id={props.id}
              values={projectIds}
              onChange={setProjectIds}
              options={options}
              invalid={Boolean(fieldErrors.projectIds)}
              summaryNoun={{ singular: 'project', plural: 'projects' }}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}

function NumberField({
  label,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} error={error}>
      {(props) => (
        <Input
          {...props}
          inputMode="decimal"
          value={value}
          invalid={Boolean(error)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="tabular-nums"
        />
      )}
    </Field>
  );
}
